<template>
  <PageHeaderLayout>
    <div
      class="main-page-content auth-emp-page"
      v-loading="loading"
      element-loading-text="加载中..."
    >
      <div class="page-header">
        <div>
          <div class="page-title">实名认证</div>
          <div class="page-desc">
            当前页面按原始业务逻辑展示企业实名认证状态，并提供灵工业务认证入口。
          </div>
        </div>
        <el-button type="primary" plain @click="fetchStatus">刷新状态</el-button>
      </div>

      <el-alert
        :title="summaryText"
        type="info"
        :closable="false"
        show-icon
        class="summary-alert"
      />

      <el-row :gutter="16">
        <el-col :span="24">
          <el-card shadow="never" class="auth-card">
            <div slot="header" class="card-header">
              <span>企业实名认证</span>
              <el-tag :type="realAuthTag.type" size="mini">
                {{ realAuthTag.text }}
              </el-tag>
            </div>

            <div class="auth-choice-list">
              <div
                v-if="Number(statusData.real_status || 0) === 0"
                class="auth-choice-card"
                @click="showAuth(1)"
              >
                <div class="auth-choice-title">我是法定代表人</div>
                <div class="auth-choice-desc">所需时长：1天以内</div>
              </div>

              <div
                v-else
                class="auth-choice-card"
                @click="showAuth(currentRealAuthType, statusData)"
              >
                <div class="auth-choice-title">
                  {{ currentRealAuthType === 1 ? "法人实名" : "经办人实名" }}
                  <span class="auth-status-inline">({{ realAuthDisplayStatus }})</span>
                  <el-button
                    v-if="showReAuthButton"
                    type="text"
                    class="reauth-btn"
                    @click.stop="handleReAuth"
                  >
                    重新授权
                  </el-button>
                </div>
                <div class="auth-choice-desc">{{ realAuthDurationText }}</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :span="24">
          <el-card shadow="never" class="auth-card">
            <div slot="header" class="card-header">
              <span>灵工业务认证</span>
              <el-tag :type="lgStatusTag.type" size="mini">
                {{ lgStatusTag.text }}
              </el-tag>
            </div>

            <div
              :class="['lg-card', { disabled: !getLgCan() }]"
              @click="eventToLg"
            >
              <div class="lg-card-content">
                <div class="lg-card-title">
                  灵活用工业务认证（{{ lgAuthDisplayStatus }}）
                </div>
                <div class="lg-card-desc">
                  完成企业实名认证后，可继续进入灵工业务认证流程。
                </div>
              </div>
              <el-button
                v-if="Number(statusData.lg_examine_auth_status) === 2"
                type="text"
                class="reason-btn"
                @click.stop="eventSeeReason"
              >
                查看原因
              </el-button>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <el-card shadow="never" class="auth-card">
        <div slot="header" class="card-header">
          <span>当前状态</span>
        </div>

        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="企业名称">
            {{ companyName }}
          </el-descriptions-item>
          <el-descriptions-item label="实名方式">
            {{ currentRealAuthType === 1 ? "法定代表人" : "经办人" }}
          </el-descriptions-item>
          <el-descriptions-item label="实名认证状态">
            {{ realAuthDisplayStatus }}
          </el-descriptions-item>
          <el-descriptions-item label="灵工业务认证">
            {{ lgAuthDisplayStatus }}
          </el-descriptions-item>
          <el-descriptions-item label="授权有效期">
            {{ realAuthDurationText }}
          </el-descriptions-item>
          <el-descriptions-item label="拒绝原因">
            {{ lgAuthReasonText }}
          </el-descriptions-item>
        </el-descriptions>
      </el-card>

      <el-dialog
        title="企业实名认证"
        :visible.sync="authDialogVisible"
        width="640px"
        destroy-on-close
      >
        <el-form
          ref="authForm"
          :model="empForm"
          :rules="empRules"
          label-width="120px"
        >
          <el-form-item label="认证方式" prop="realAuthType">
            <el-radio-group v-model="empForm.realAuthType">
              <el-radio :label="1">法定代表人</el-radio>
              <el-radio v-if="empForm.realAuthType === 2" :label="2" disabled>
                经办人
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="公司名称" prop="companyName">
            <el-input v-model.trim="empForm.companyName" />
          </el-form-item>
          <el-form-item label="统一社会信用代码" prop="taxpayer">
            <el-input v-model.trim="empForm.taxpayer" />
          </el-form-item>
          <el-form-item label="法人姓名" prop="legalName">
            <el-input v-model.trim="empForm.legalName" />
          </el-form-item>
          <el-form-item label="法人身份证号" prop="legalIdCard">
            <el-input v-model.trim="empForm.legalIdCard" />
          </el-form-item>
          <template v-if="empForm.realAuthType === 2">
            <el-form-item label="经办人姓名" prop="userName">
              <el-input v-model.trim="empForm.userName" />
            </el-form-item>
            <el-form-item label="经办人手机号" prop="mobile">
              <el-input v-model.trim="empForm.mobile" />
            </el-form-item>
            <el-form-item label="经办人身份证号" prop="idCard">
              <el-input v-model.trim="empForm.idCard" />
            </el-form-item>
          </template>
        </el-form>
        <div slot="footer">
          <el-button @click="authDialogVisible = false">取消</el-button>
          <el-button
            type="primary"
            :loading="submitLoading.real"
            @click="eventConfirmRealAuth"
          >
            确定
          </el-button>
        </div>
      </el-dialog>

      <el-dialog
        title="查看原因"
        :visible.sync="reasonDialogVisible"
        width="520px"
      >
        <div class="reason-content" v-html="lgAuthReasonHtml" />
        <div slot="footer">
          <el-button @click="reasonDialogVisible = false">关闭</el-button>
        </div>
      </el-dialog>
    </div>
  </PageHeaderLayout>
</template>

<script>
import PageHeaderLayout from "@/layouts/PageHeaderLayout";

const DEFAULT_STATUS = "未认证";

export default {
  name: "AuthEmp",
  components: {
    PageHeaderLayout,
  },
  data() {
    return {
      loading: false,
      authDialogVisible: false,
      reasonDialogVisible: false,
      statusData: {},
      submitLoading: {
        real: false,
      },
      empForm: {
        realAuthType: 1,
        companyName: "",
        taxpayer: "",
        legalName: "",
        legalIdCard: "",
        userName: "",
        mobile: "",
        idCard: "",
      },
    };
  },
  computed: {
    empRules() {
      const transactorRequired = this.empForm.realAuthType === 2;
      return {
        realAuthType: [{ required: true, message: "请选择认证方式", trigger: "change" }],
        companyName: [{ required: true, message: "请输入公司名称", trigger: "blur" }],
        taxpayer: [
          { required: true, message: "请输入统一社会信用代码", trigger: "blur" },
        ],
        legalName: [{ required: true, message: "请输入法人姓名", trigger: "blur" }],
        legalIdCard: [
          { required: true, message: "请输入法人身份证号", trigger: "blur" },
        ],
        userName: transactorRequired
          ? [{ required: true, message: "请输入经办人姓名", trigger: "blur" }]
          : [],
        mobile: transactorRequired
          ? [{ required: true, message: "请输入经办人手机号", trigger: "blur" }]
          : [],
        idCard: transactorRequired
          ? [{ required: true, message: "请输入经办人身份证号", trigger: "blur" }]
          : [],
      };
    },
    companyName() {
      return (
        this.pickValue([
          "company_name",
          "companyName",
          "corp_name",
          "name",
          "auth_info.companyName",
          "auth_info.company_name",
        ]) ||
        this.getUserInfoValue(["company_name", "companyName", "name"]) ||
        "-"
      );
    },
    currentRealAuthType() {
      return Number(
        this.pickValue(["auth_info.real_auth_type", "auth_info.realAuthType"]) || 1
      );
    },
    realAuthDisplayStatus() {
      if (Number(this.statusData.real_status) === 1) {
        return this.mapEmpowerStatusText(this.statusData.empower_status);
      }
      return this.mapRealStatusText(this.statusData.real_status);
    },
    realAuthDurationText() {
      if (
        Number(this.statusData.real_status) === 1 &&
        Number(this.statusData.empower_status) === 1
      ) {
        const start = this.statusData.empower_auth_start_time || "-";
        const end = this.statusData.empower_auth_end_time || "-";
        return `${start}至${end}`;
      }
      return this.currentRealAuthType === 1 ? "所需时长：1天以内" : "所需时长：1-3天";
    },
    showReAuthButton() {
      if (Number(this.statusData.real_status) === 1) {
        return [3, 4].includes(Number(this.statusData.empower_status));
      }
      return Number(this.statusData.real_status) === 3;
    },
    lgAuthDisplayStatus() {
      return this.mapLgStatusText(this.statusData.lg_examine_auth_status);
    },
    lgStatusTag() {
      return this.createStatusTag(this.lgAuthDisplayStatus);
    },
    realAuthTag() {
      return this.createStatusTag(this.realAuthDisplayStatus);
    },
    lgAuthReasonHtml() {
      return this.statusData.lg_examine_auth_message || "暂无原因说明";
    },
    lgAuthReasonText() {
      return this.statusData.lg_examine_auth_message || "-";
    },
    summaryText() {
      return `当前企业：${this.companyName}；实名认证：${this.realAuthDisplayStatus}；灵工业务认证：${this.lgAuthDisplayStatus}`;
    },
  },
  created() {
    this.fetchStatus();
  },
  methods: {
    getUserInfoValue(keys) {
      let userInfo = {};
      try {
        userInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
      } catch (error) {
        userInfo = {};
      }
      for (let i = 0; i < keys.length; i += 1) {
        const value = userInfo[keys[i]];
        if (value !== undefined && value !== null && value !== "") {
          return value;
        }
      }
      return "";
    },
    getValueByPath(source, path) {
      return path.split(".").reduce((result, key) => {
        if (result === null || typeof result === "undefined") {
          return undefined;
        }
        return result[key];
      }, source);
    },
    pickValue(paths) {
      for (let i = 0; i < paths.length; i += 1) {
        const value = this.getValueByPath(this.statusData, paths[i]);
        if (value !== undefined && value !== null && value !== "") {
          return value;
        }
      }
      return "";
    },
    createStatusTag(text) {
      const value = String(text || DEFAULT_STATUS);
      if (/成功|完成|通过|已认证|已授权/.test(value)) {
        return { text: value, type: "success" };
      }
      if (/审核中|进行中|待处理|待授权|待认证|申请中|认证中|授权中/.test(value)) {
        return { text: value, type: "warning" };
      }
      if (/失败|驳回|拒绝|异常|未通过|超时|过期/.test(value)) {
        return { text: value, type: "danger" };
      }
      return { text: value, type: "info" };
    },
    mapRealStatusText(status) {
      const statusMap = {
        0: "待授权",
        1: "授权通过",
        2: "授权失败",
        3: "授权超时",
        5: "认证中",
      };
      return statusMap[Number(status)] || DEFAULT_STATUS;
    },
    mapEmpowerStatusText(status) {
      const statusMap = {
        0: "待授权",
        1: "授权通过",
        2: "授权失败",
        3: "授权超时",
        4: "授权过期",
        5: "认证中",
      };
      return statusMap[Number(status)] || DEFAULT_STATUS;
    },
    mapLgStatusText(status) {
      const statusMap = {
        "-1": "未认证",
        0: "认证中",
        1: "认证中",
        2: "认证拒绝",
        3: "认证成功",
      };
      return statusMap[String(status)] || DEFAULT_STATUS;
    },
    syncAuthForm(realAuthType, authInfo = {}) {
      const transactorInfo = authInfo.transactorInfo || {};
      this.empForm = {
        realAuthType: Number(realAuthType || 1),
        companyName:
          authInfo.companyName ||
          authInfo.company_name ||
          this.companyName ||
          "",
        taxpayer: authInfo.taxpayer || "",
        legalName: authInfo.legalName || "",
        legalIdCard: authInfo.legalIdCard || "",
        userName: transactorInfo.userName || authInfo.userName || "",
        mobile: transactorInfo.mobile || authInfo.mobile || "",
        idCard: transactorInfo.idCard || authInfo.idCard || "",
      };
    },
    async fetchStatus() {
      this.loading = true;
      try {
        const result = await this.$api.getAuthStatus();
        this.statusData = result || {};
      } finally {
        this.loading = false;
      }
    },
    showAuth(realAuthType, statusObj) {
      if (
        statusObj &&
        Number(statusObj.real_status) === 1 &&
        Number(statusObj.empower_status) === 1
      ) {
        this.$message.warning("已认证，无需额外操作");
        return;
      }
      const authInfo = (statusObj && statusObj.auth_info) || {};
      this.syncAuthForm(realAuthType, authInfo);
      this.authDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.authForm) {
          this.$refs.authForm.clearValidate();
        }
      });
    },
    handleReAuth() {
      this.showAuth(this.currentRealAuthType, this.statusData);
    },
    getLgCan() {
      return (
        Number(this.statusData.real_status) === 1 &&
        Number(this.statusData.empower_status) === 1
      );
    },
    eventSeeReason() {
      const canShowReason =
        Number(this.statusData.real_status) === 1 &&
        Number(this.statusData.empower_status) === 1 &&
        Number(this.statusData.seal_status) === 1 &&
        Number(this.statusData.lg_examine_auth_status) === 2;
      if (canShowReason) {
        this.reasonDialogVisible = true;
      }
    },
    eventToLg() {
      if ([0, 1].includes(Number(this.statusData.lg_examine_auth_status))) {
        this.$message.warning("正在认证中，无需额外操作");
        return;
      }
      const canJump =
        Number(this.statusData.real_status) === 1 &&
        Number(this.statusData.empower_status) === 1 &&
        [-1, 2].includes(Number(this.statusData.lg_examine_auth_status));
      if (canJump) {
        this.$router.push({
          path: "/auth_nlg",
          query: {
            seal_url: this.statusData.seal_url,
            seal_name: this.statusData.seal_name,
            seal_id: this.statusData.seal_id,
          },
        });
      }
    },
    eventConfirmRealAuth() {
      if (this.submitLoading.real) {
        return;
      }
      this.$refs.authForm.validate(async (valid) => {
        if (!valid) {
          return false;
        }
        const params = {
          realAuthType: this.empForm.realAuthType,
          companyName: this.empForm.companyName,
          taxpayer: this.empForm.taxpayer,
          legalName: this.empForm.legalName,
          legalIdCard: this.empForm.legalIdCard,
          userName: this.empForm.userName,
          mobile: this.empForm.mobile,
          idCard: this.empForm.idCard,
        };
        if (Number(params.realAuthType) === 2) {
          params.transactorInfo = {
            userName: params.userName,
            mobile: params.mobile,
            idCard: params.idCard,
          };
          delete params.userName;
          delete params.mobile;
          delete params.idCard;
        }
        this.submitLoading.real = true;
        try {
          const result = await this.$api.postRealAuth(params);
          this.authDialogVisible = false;
          if (result && result.authUrl) {
            window.open(result.authUrl, "_blank");
          }
          this.$alert("授权完成后，请刷新页面", "提示", {
            showClose: false,
            confirmButtonText: "刷新",
            callback: () => {
              window.location.reload();
            },
          });
        } finally {
          this.submitLoading.real = false;
        }
        return true;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.auth-emp-page {
  min-height: 100%;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.page-desc {
  margin-top: 8px;
  color: #909399;
  line-height: 20px;
}

.summary-alert {
  margin-bottom: 16px;
}

.auth-card {
  margin-bottom: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.auth-choice-list {
  display: flex;
  flex-wrap: wrap;
}

.auth-choice-card,
.lg-card {
  width: 420px;
  min-height: 180px;
  border-radius: 10px;
  padding: 24px;
  box-sizing: border-box;
  background: linear-gradient(135deg, #277dfd 0%, #00a9ff 100%);
  box-shadow: 10px 10px 40px #e7e7e7;
  color: #fff;
  cursor: pointer;
}

.auth-choice-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.auth-choice-title,
.lg-card-title {
  font-size: 18px;
  line-height: 28px;
}

.auth-choice-desc,
.lg-card-desc {
  margin-top: 20px;
  font-size: 13px;
  line-height: 22px;
}

.auth-status-inline {
  margin-left: 4px;
}

.reauth-btn,
.reason-btn {
  margin-left: 12px;
  padding: 0;
  color: #fff;
}

.reauth-btn:hover,
.reason-btn:hover,
.reauth-btn:focus,
.reason-btn:focus {
  color: #e6f7ff;
}

.lg-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.lg-card-content {
  padding-right: 16px;
}

.disabled {
  opacity: 0.5;
}

.reason-content {
  line-height: 24px;
  color: #606266;
  word-break: break-word;
}
</style>
